/** Tag-category JSON-schema vocabulary — the console-side single source of truth.
 *
 * Mirrors `backend/demo/lib/tag_category_sets/schema_vocabulary.py`. Beyond
 * standard JSON Schema (Draft 2020-12) the tag schemas carry custom `x-`
 * extensions for property groups, multilingual fields, and Django-template /
 * HTML content flags. The vocabulary is intentionally open — unknown `x-` keys
 * round-trip untouched so it can grow without breaking older schemas.
 *
 * These types + helpers are shared by the SchemaEditor (Phase 1) and the
 * schema-driven tag data editor (Phase 3).
 */

// --- Extension keys ---------------------------------------------------------
export const X_GROUPS = 'x-groups' as const;
export const X_GROUP = 'x-group' as const;
export const X_DISPLAY_ORDER = 'x-displayOrder' as const;
export const X_MULTILINGUAL = 'x-multilingual' as const;
export const X_TEMPLATE = 'x-template' as const;
export const X_HTML = 'x-html' as const;
export const X_ENUM_LABELS = 'x-enum-labels' as const;

export type JsonSchemaType = 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array';

export const SCHEMA_TYPES: JsonSchemaType[] = [
  'string',
  'number',
  'integer',
  'boolean',
  'object',
  'array',
];

/** A declared logical property group (schema-level `x-groups[i]`). */
export interface SchemaGroup {
  name: string;
  title?: string;
  description?: string;
  order?: number;
}

/** One JSON-schema property node (recursive), with our `x-` extensions typed. */
export interface SchemaProp {
  type?: JsonSchemaType;
  title?: string;
  description?: string;
  format?: string;
  enum?: unknown[];
  default?: unknown;
  properties?: Record<string, SchemaProp>;
  items?: SchemaProp;
  [X_GROUP]?: string;
  [X_DISPLAY_ORDER]?: number;
  [X_MULTILINGUAL]?: boolean;
  [X_TEMPLATE]?: boolean;
  [X_HTML]?: boolean;
  [X_ENUM_LABELS]?: string[];
  // Preserve any other keyword (const, minimum, pattern, additionalProperties…).
  [k: string]: unknown;
}

/** A tag-category schema: a JSON-schema object node with our extensions. */
export interface TagSchema {
  type?: string;
  properties?: Record<string, SchemaProp>;
  required?: string[];
  [X_GROUPS]?: SchemaGroup[];
  [k: string]: unknown;
}

/** Sentinel group id for properties without an `x-group`. */
export const UNGROUPED = '__ungrouped__';

// --- Read helpers -----------------------------------------------------------

export function groupsOf(schema: TagSchema | null | undefined): SchemaGroup[] {
  const raw = schema?.[X_GROUPS];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((g): g is SchemaGroup => !!g && typeof g === 'object' && typeof g.name === 'string')
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function isMultilingual(prop: SchemaProp | null | undefined): boolean {
  return prop?.[X_MULTILINGUAL] === true;
}
export function isTemplate(prop: SchemaProp | null | undefined): boolean {
  return prop?.[X_TEMPLATE] === true;
}
export function isHtml(prop: SchemaProp | null | undefined): boolean {
  return prop?.[X_HTML] === true;
}

export function isRequired(schema: TagSchema | null | undefined, key: string): boolean {
  return Array.isArray(schema?.required) && schema!.required!.includes(key);
}

/** Property entries sorted by `x-displayOrder` then key (the dashboard's rule). */
export function sortedPropEntries(
  props: Record<string, SchemaProp> | undefined,
): [string, SchemaProp][] {
  const entries = Object.entries(props ?? {});
  return entries.sort(([ak, av], [bk, bv]) => {
    const ao = av?.[X_DISPLAY_ORDER];
    const bo = bv?.[X_DISPLAY_ORDER];
    if (ao !== undefined && bo !== undefined) return ao - bo;
    if (ao !== undefined) return -1;
    if (bo !== undefined) return 1;
    return ak.localeCompare(bk);
  });
}

/** A property list partitioned into declared groups (in `x-groups` order),
 *  each carrying its member properties; a trailing "Ungrouped" bucket collects
 *  properties whose `x-group` is missing or unknown. Buckets with no members
 *  are omitted (except Ungrouped, which callers can filter). */
export interface GroupedProps {
  group: SchemaGroup | null; // null ⇒ the Ungrouped bucket
  entries: [string, SchemaProp][];
}

export function groupedProps(schema: TagSchema | null | undefined): GroupedProps[] {
  const groups = groupsOf(schema);
  const entries = sortedPropEntries(schema?.properties);
  const known = new Set(groups.map((g) => g.name));
  const buckets = new Map<string, [string, SchemaProp][]>();
  const ungrouped: [string, SchemaProp][] = [];

  for (const [key, prop] of entries) {
    const g = prop?.[X_GROUP];
    if (typeof g === 'string' && known.has(g)) {
      const arr = buckets.get(g) ?? [];
      arr.push([key, prop]);
      buckets.set(g, arr);
    } else {
      ungrouped.push([key, prop]);
    }
  }

  const out: GroupedProps[] = [];
  for (const g of groups) {
    const arr = buckets.get(g.name);
    if (arr && arr.length) out.push({ group: g, entries: arr });
  }
  if (ungrouped.length) out.push({ group: null, entries: ungrouped });
  return out;
}

// --- Language list (multilingual widget; Phase 3) ---------------------------
// A pragmatic subset of ISO-639-1 covering the operators' guest languages. The
// backend normalizes stored codes via get_language_code / Language_Alias; this
// list only seeds the editor's "add language" picker.
export interface LanguageOption {
  code: string;
  label: string;
}
export const COMMON_LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'nl', label: 'Dutch' },
  { code: 'ja', label: 'Japanese' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ko', label: 'Korean' },
  { code: 'ru', label: 'Russian' },
  { code: 'ar', label: 'Arabic' },
  { code: 'pl', label: 'Polish' },
  { code: 'tr', label: 'Turkish' },
  { code: 'sv', label: 'Swedish' },
  { code: 'da', label: 'Danish' },
  { code: 'nb', label: 'Norwegian' },
  { code: 'fi', label: 'Finnish' },
  { code: 'el', label: 'Greek' },
  { code: 'he', label: 'Hebrew' },
];

export function languageLabel(code: string): string {
  return COMMON_LANGUAGES.find((l) => l.code === code)?.label ?? code;
}
